<script setup>
import { ref, computed, defineAsyncComponent } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { useCmsUiVisible } from '@/composables/useCmsUiVisible.js'
import { isReviewLive } from '@/cms/reviewGate.js'
import {
  SITE_URL,
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_IMAGE_WIDTH,
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_TYPE,
  DEFAULT_OG_IMAGE_ALT,
  withTrailingSlash
} from '@/utils/siteUrl'
import ContentHero from '@/components/content/ContentHero.vue'
import OurWorkSidebar from '@/components/OurWorkSidebar.vue'
import ResourceCard from '@/components/ResourceCard.vue'
import ProjectPartners from '@/components/ProjectPartners.vue'
import projectParties from '@/content/project-parties.yml'

const isCmsEnabled = import.meta.env.MODE === 'cms'
const CmsEditorModal = isCmsEnabled
  ? defineAsyncComponent(() => import('@/cms/components/CmsEditorModal.vue'))
  : null

const { cmsUiVisible } = useCmsUiVisible()
const showCmsChrome = computed(() => isCmsEnabled && cmsUiVisible.value)

const route = useRoute()

const modules = import.meta.glob('/src/content/our-work/*.md', { eager: true })
const resourceModules = import.meta.glob('/src/content/resources/*.md', { eager: true })
const slug = computed(() => route.params.slug)

const isEditing   = ref(false)
const contentPath = computed(() => `/src/content/our-work/${slug.value}.md`)

const frontmatter = computed(() => {
  const mod = modules[contentPath.value]
  if (!mod) return null
  return {
    title: mod.title,
    date: mod.date,
    excerpt: mod.excerpt,
    // Not rendered on this page any more (jk-34 removed the main image),
    // but still the per-project og:image source (jk-35) and the /our-work
    // listing card thumbnail — kept in the frontmatter for those.
    image: mod.image || '',
    projectLeader: mod.projectLeader || '',
    publishedAt: mod.publishedAt || null,
    reviewStatus: mod.reviewStatus || '',
    projectId: mod.projectId || '',
  }
})

const partners = computed(() => projectParties[frontmatter.value?.projectId]?.parties || [])

const relatedResources = computed(() => {
  const id = frontmatter.value?.projectId
  if (!id) return []
  return Object.entries(resourceModules)
    .filter(([, mod]) => mod.projectId === id)
    .map(([path, mod]) => ({
      slug:        path.replace('/src/content/resources/', '').replace('.md', ''),
      title:       mod.title,
      description: mod.description,
      type:        mod.type,
      topics:      mod.topics || [],
      publishedOn: mod.publishedOn || mod.publishedAt || '',
      externalUrl: mod.externalUrl || '',
      goInternal:  mod.goInternal || false,
      thumbnail:   mod.thumbnail || '',
    }))
})

const isPublished = computed(() => {
  if (!frontmatter.value) return true
  if (import.meta.env.DEV) return true
  const p = frontmatter.value.publishedAt
  return (!p || new Date(p) <= new Date()) && isReviewLive(frontmatter.value.reviewStatus)
})

const breadcrumbs = computed(() => [
  { label: 'Home', to: '/' },
  { label: 'Our work', to: '/our-work' },
  { label: frontmatter.value?.title || 'Project' },
])

const baseUrl = import.meta.env.VITE_APP_BASE_URL || SITE_URL

// Sitewide default share image (jk-35) unless this project sets its own image.
useHead(computed(() => {
  const fm = frontmatter.value
  const title = fm ? fm.title : 'Our work'
  const description = fm?.excerpt || ''
  const url = `${baseUrl}${withTrailingSlash(`/our-work/${slug.value}`)}`
  const hasCustomImage = Boolean(fm?.image)
  const ogImage = fm?.image || DEFAULT_OG_IMAGE
  const ogImageAbs = `${baseUrl}${ogImage}`
  return {
    title,
    meta: [
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: url },
      { property: 'og:image', content: ogImageAbs },
      { name: 'twitter:image', content: ogImageAbs },
      // Known dimensions only apply to the default image — a project's own
      // image may be a different size, so we don't claim to know it.
      ...(hasCustomImage ? [] : [
        { property: 'og:image:width', content: DEFAULT_OG_IMAGE_WIDTH },
        { property: 'og:image:height', content: DEFAULT_OG_IMAGE_HEIGHT },
        { property: 'og:image:type', content: DEFAULT_OG_IMAGE_TYPE },
        { property: 'og:image:alt', content: DEFAULT_OG_IMAGE_ALT }
      ]),
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
    ],
    link: [{ rel: 'canonical', href: url }],
  }
}))

const lazyModules = import.meta.glob('/src/content/our-work/*.md')
const MarkdownContent = computed(() => {
  const key = `/src/content/our-work/${slug.value}.md`
  if (!lazyModules[key]) return null
  return defineAsyncComponent(lazyModules[key])
})

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'long', year: 'numeric'
  })
}

const heroMeta = computed(() => {
  if (!frontmatter.value) return {}
  return {
    intro: frontmatter.value.excerpt,
    published: '',   // date intentionally hidden on Our Work pages (see 07c0291)
    topic: '',       // "Led by …" intentionally hidden (see 07c0291)
  }
})

const tocItems = computed(() => {
  const items = [{ id: 'overview', label: 'Overview' }]
  if (partners.value.length) items.push({ id: 'project-partners', label: 'Project partners' })
  if (relatedResources.value.length) items.push({ id: 'related-resources', label: 'Related resources' })
  return items
})
</script>

<template>
  <div class="our-work-detail">
    <template v-if="!frontmatter">
      <div class="container state">
        <h1>Not found</h1>
        <RouterLink to="/our-work/">← Back to our work</RouterLink>
      </div>
    </template>

    <template v-else-if="!isPublished">
      <div class="container state">
        <p class="not-published">This item is not yet available.</p>
        <div class="back"><RouterLink to="/our-work/">← Back to our work</RouterLink></div>
      </div>
    </template>

    <template v-else>
      <div v-if="showCmsChrome" class="container cms-bar">
        <button class="edit-inline" @click="isEditing = true">Edit</button>
      </div>
      <CmsEditorModal v-if="isCmsEnabled && isEditing" :content-path="contentPath" @close="isEditing = false" />

      <ContentHero
        :title="frontmatter.title"
        colour="pink"
        :hero="heroMeta"
        :breadcrumbs="breadcrumbs"
      />

      <div class="container body-grid">
        <div class="nav-col">
          <OurWorkSidebar :tocItems="tocItems" />
        </div>

        <div class="content-col">
          <section id="overview" class="block">
            <component :is="MarkdownContent" v-if="MarkdownContent" class="body" />
          </section>

          <section v-if="partners.length" id="project-partners" class="block">
            <h2>Project partners</h2>
            <ProjectPartners :partners="partners" />
          </section>

          <section v-if="relatedResources.length" id="related-resources" class="block">
            <h2>Related resources</h2>
            <div class="cards">
              <ResourceCard
                v-for="(resource, index) in relatedResources"
                :key="resource.slug"
                :slug="resource.slug"
                :title="resource.title"
                :description="resource.description"
                :type="resource.type"
                :topics="resource.topics"
                :publishedOn="resource.publishedOn"
                :externalUrl="resource.externalUrl"
                :goInternal="resource.goInternal"
                :thumbnail="resource.thumbnail"
                :colorIndex="index"
              />
            </div>
          </section>
        </div>
      </div>

      <div class="container back"><RouterLink to="/our-work/">← Back to our work</RouterLink></div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.our-work-detail {
  padding-bottom: $space-10;

  .state { max-width: $content-prose; padding-block: $space-8; }

  .cms-bar {
    display: flex;
    justify-content: flex-end;
    padding-top: $space-4;
  }

  .edit-inline {
    background: none;
    border: 1px solid var(--color-border);
    border-radius: $radius-sm;
    color: var(--color-text-muted);
    cursor: pointer;
    font-family: $font-sans;
    font-size: $font-size-cap;
    font-weight: 600;
    padding: 2px $space-2;
    @include transition(color, border-color, background);

    &:hover         { color: var(--color-primary); border-color: var(--color-primary); }
    &:focus-visible { @include focus-ring; }
    &:active        { transform: translateY(1px); }
  }

  // Two-column layout below the hero: sticky nav column (On this page TOC,
  // via OurWorkSidebar) beside the content column. Single column
  // below $bp-lg — mirrors OurApproach.vue's body-grid, including its
  // from-lg ratio fix (jk-31 follow-up) to match SplitHeaderBlock's own
  // proportional split site-wide, rather than a fixed 280px column.
  .body-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: $space-6;
    // Margin, not padding — collapses with ContentHero's own trailing
    // margin-bottom ($space-8) instead of the two summing to 128px; the
    // larger of the two wins (here, they're equal, so 64px net).
    margin-top: $space-8;

    @include from-lg {
      grid-template-columns: minmax(0, 2fr) minmax(0, 5fr);
      gap: $space-8;
    }
  }

  .nav-col {
    @include from-lg {
      position: sticky;
      top: $space-6;
      align-self: start;
    }
  }

  .content-col {
    // No max-width — fills its grid track (1fr) so it lines up with
    // ContentHero's band above, which spans the full .container width.
    // Previously capped at $content-prose (760px), well short of the
    // ~1100px+ the 1fr track actually has at from-lg, reading as oddly
    // narrow against the full-width hero.
    min-width: 0;
  }

  // Anchored content-col sections — hairline-divided rhythm; scroll offset
  // clears the top edge when jumped to from the TOC.
  .block {
    scroll-margin-top: $space-6;

    & + .block {
      margin-top: $space-8;
      padding-top: $space-8;
      border-top: 1px solid var(--color-border);
    }

    h2 {
      margin: 0 0 $space-4;
      color: var(--color-ink);
      font-size: $font-size-h2;
      line-height: $lh-heading;
      letter-spacing: $ls-heading;
      font-weight: 500;
    }
  }

  .body { line-height: $lh-base; color: var(--color-text); }

  .cards {
    display: flex;
    flex-direction: column;
    gap: $space-4;
  }

  .not-published {
    padding: $space-10 0;
    color: var(--color-text-muted);
    font-style: italic;
  }

  .back {
    margin-top: $space-10;
    padding-top: $space-6;
    border-top: 1px solid var(--color-border);
    font-size: $font-size-sm;
    a { color: var(--color-primary); text-decoration: none; &:hover { text-decoration: underline; } }
  }
}
</style>
